import { createSlice } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { ReleaseNotification } from '../../types/release-notes/release-notes';
import {
  fetchNextPageAll,
  fetchNextPageUnread,
  updateNotification,
  markAllNotificationsAsRead,
  refetchUnreadNotifications,
  refetchAllNotifications,
} from './releaseNotifications.action';
import { NOTIFICATION_LIMIT } from './releaseNotifications.config';

export interface ReleaseNotificationsState {
  statusUnread: FETCH_STATUS;
  nextPageUnread: number;
  totalPagesUnread: number | null;
  totalItemsUnread: number | null;
  itemsUnread: ReleaseNotification[];

  statusAll: FETCH_STATUS;
  nextPageAll: number;
  totalPagesAll: number | null;
  totalItemsAll: number | null;
  itemsAll: ReleaseNotification[];

  updateNotificationStatus: FETCH_STATUS;
  markAllNotificationsAsReadStatus: FETCH_STATUS;
}

const INIT_STATE: ReleaseNotificationsState = {
  statusUnread: FETCH_STATUS.NULL,
  nextPageUnread: 1,
  totalPagesUnread: null,
  totalItemsUnread: null,
  itemsUnread: [],

  statusAll: FETCH_STATUS.NULL,
  nextPageAll: 1,
  totalPagesAll: null,
  totalItemsAll: null,
  itemsAll: [],

  updateNotificationStatus: FETCH_STATUS.NULL,
  markAllNotificationsAsReadStatus: FETCH_STATUS.NULL,
};

const releaseNotificationsSlice = createSlice({
  name: 'releaseNotifications',
  initialState: INIT_STATE,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchNextPageUnread.pending, state => {
        state.statusUnread = FETCH_STATUS.PENDING;
      })
      .addCase(fetchNextPageUnread.rejected, state => {
        state.statusUnread = FETCH_STATUS.REJECTED;
      })
      .addCase(fetchNextPageUnread.fulfilled, (state, action) => {
        state.itemsUnread.splice(
          (state.nextPageUnread - 1) * NOTIFICATION_LIMIT,
          NOTIFICATION_LIMIT,
          ...action.payload.items
        );
        state.totalItemsUnread = action.payload.meta.totalItems;
        state.totalPagesUnread = action.payload.meta.totalPages;
        state.nextPageUnread += 1;
        state.statusUnread = FETCH_STATUS.NULL;
      });

    builder
      .addCase(fetchNextPageAll.pending, state => {
        state.statusAll = FETCH_STATUS.PENDING;
      })
      .addCase(fetchNextPageAll.rejected, state => {
        state.statusAll = FETCH_STATUS.REJECTED;
      })
      .addCase(fetchNextPageAll.fulfilled, (state, action) => {
        state.itemsAll.splice(
          (state.nextPageAll - 1) * NOTIFICATION_LIMIT,
          NOTIFICATION_LIMIT,
          ...action.payload.items
        );
        state.totalItemsAll = action.payload.meta.totalItems;
        state.totalPagesAll = action.payload.meta.totalPages;
        state.nextPageAll += 1;
        state.statusAll = FETCH_STATUS.NULL;
      });

    builder
      .addCase(updateNotification.pending, state => {
        state.updateNotificationStatus = FETCH_STATUS.PENDING;
      })
      .addCase(updateNotification.rejected, state => {
        state.updateNotificationStatus = FETCH_STATUS.REJECTED;
      })
      .addCase(updateNotification.fulfilled, (state, action) => {
        state.updateNotificationStatus = FETCH_STATUS.FULFILLED;

        const item = state.itemsAll.find(n => n.id === action.meta.arg.notificationId);
        if (item) item.isRead = action.meta.arg.isRead;
      });

    builder
      .addCase(markAllNotificationsAsRead.pending, state => {
        state.markAllNotificationsAsReadStatus = FETCH_STATUS.PENDING;
      })
      .addCase(markAllNotificationsAsRead.rejected, state => {
        state.markAllNotificationsAsReadStatus = FETCH_STATUS.REJECTED;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, state => {
        state.markAllNotificationsAsReadStatus = FETCH_STATUS.FULFILLED;

        state.totalPagesUnread = 0;
        state.totalItemsUnread = 0;
        state.itemsUnread = [];

        state.itemsAll.forEach(n => (n.isRead = true));
      });

    builder
      .addCase(refetchUnreadNotifications.pending, state => {
        state.statusUnread = FETCH_STATUS.PENDING;
      })
      .addCase(refetchUnreadNotifications.rejected, state => {
        state.statusUnread = FETCH_STATUS.REJECTED;
      })
      .addCase(refetchUnreadNotifications.fulfilled, (state, action) => {
        state.itemsUnread = action.payload.items;
        state.totalItemsUnread = action.payload.meta.totalItems;
        state.totalPagesUnread = action.payload.meta.totalPages;
        state.nextPageUnread = Math.ceil(action.payload.items.length / NOTIFICATION_LIMIT) + 1;
        state.statusUnread = FETCH_STATUS.NULL;
      });

    builder
      .addCase(refetchAllNotifications.pending, state => {
        state.statusAll = FETCH_STATUS.PENDING;
      })
      .addCase(refetchAllNotifications.rejected, state => {
        state.statusAll = FETCH_STATUS.REJECTED;
      })
      .addCase(refetchAllNotifications.fulfilled, (state, action) => {
        state.itemsAll = action.payload.items;
        state.totalItemsAll = action.payload.meta.totalItems;
        state.totalPagesAll = action.payload.meta.totalPages;
        state.nextPageAll = Math.ceil(action.payload.items.length / NOTIFICATION_LIMIT) + 1;
        state.statusAll = FETCH_STATUS.NULL;
      });
  },
});

export default releaseNotificationsSlice.reducer;
